// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  LegacyDecisionTree,
  LegacyTreeNode,
  LegacyNodeType,
  addChild,
  removeNode,
  updateNode,
  createNode,
} from '../../lib/tree';
import { ConfirmationDialog } from '../VaccineDashboard/components/ConfirmationDialog';

interface DecisionTreeEditorProps {
  tree: LegacyDecisionTree;
  selectedNodeId: string | null;
  onTreeChange: (tree: LegacyDecisionTree) => void;
  onNodeSelect: (nodeId: string | null) => void;
}

const DecisionTreeEditor: React.FC<DecisionTreeEditorProps> = ({
  tree,
  selectedNodeId,
  onTreeChange,
  onNodeSelect
}) => {
  const [editingNode, setEditingNode] = useState<{ id: string; label: string } | null>(null);
  const [outlineExpanded, setOutlineExpanded] = useState(true);
  const [inspectorExpanded, setInspectorExpanded] = useState(true);
  const [autoCategorizedMsg, setAutoCategorizedMsg] = useState<string | null>(null);
  const [pendingRemoveNodeId, setPendingRemoveNodeId] = useState<string | null>(null);
  const openMenuRef = useRef<HTMLElement | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openMenuRef.current && !openMenuRef.current.contains(e.target as Node)) {
        openMenuRef.current.style.display = 'none';
        openMenuRef.current = null;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedNode = selectedNodeId ? findNodeInTree(tree.root, selectedNodeId) : null;

  const handleAddChild = useCallback((parentId: string, nodeType: LegacyNodeType) => {
    const child = createNode(nodeType, `New ${nodeType}`);
    onTreeChange(addChild(tree, parentId, child));
  }, [tree, onTreeChange]);

  const handleRemoveNode = useCallback((nodeId: string) => {
    setPendingRemoveNodeId(nodeId);
  }, []);

  const confirmRemoveNode = useCallback(() => {
    if (pendingRemoveNodeId) {
      onTreeChange(removeNode(tree, pendingRemoveNodeId));
      if (selectedNodeId === pendingRemoveNodeId) {
        onNodeSelect(null);
      }
    }
    setPendingRemoveNodeId(null);
  }, [pendingRemoveNodeId, tree, onTreeChange, selectedNodeId, onNodeSelect]);

  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<LegacyTreeNode>) => {
    onTreeChange(updateNode(tree, nodeId, updates));
  }, [tree, onTreeChange]);

  const updateChanceProbability = useCallback(
    (childId: string, probability: number | undefined) => {
      handleUpdateNode(childId, { p: probability });
    },
    [handleUpdateNode],
  );

  const handleNormalizeProbabilities = useCallback((nodeId: string) => {
    const node = findNodeInTree(tree.root, nodeId);
    if (!node || !node.children || node.children.length === 0) return;

    const sum = node.children.reduce((acc, c) => acc + (c.p || 0), 0);
    if (sum <= 0) return;

    let newTree = tree;
    node.children.forEach(child => {
      const newP = (child.p || 0) / sum;
      newTree = updateNode(newTree, child.id, { p: newP });
    });

    onTreeChange(newTree);
  }, [tree, onTreeChange]);

  const renderOutline = (node: LegacyTreeNode, depth: number = 0, isLastSibling: boolean = false): React.ReactNode => {
    const isSelected = selectedNodeId === node.id;
    const isEditing = editingNode?.id === node.id;

    return (
      <div key={node.id} className="outline-node" data-depth={depth} data-last-sibling={isLastSibling}>
        <div
          className={`outline-row ${isSelected ? 'selected' : ''}`}
          role="treeitem"
          aria-selected={isSelected || undefined}
          aria-level={depth + 1}
          style={{ paddingLeft: depth > 0 ? `${12 + (depth - 1) * 24}px` : '8px' }}
          data-depth={depth}
          onClick={() => onNodeSelect(node.id)}
        >
          <span className="node-icon">
            {node.type === 'decision' ? '■' : node.type === 'chance' ? '●' : '▲'}
          </span>
          <span className="node-label">
            {isEditing ? (
              <input
                type="text"
                value={editingNode?.label ?? ''}
                onChange={(e) => setEditingNode({ id: node.id, label: e.target.value })}
                onBlur={() => {
                  if (editingNode && editingNode.id === node.id) {
                    handleUpdateNode(node.id, { label: editingNode.label });
                  }
                  setEditingNode(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (editingNode && editingNode.id === node.id) {
                      handleUpdateNode(node.id, { label: editingNode.label });
                    }
                    setEditingNode(null);
                  }
                  if (e.key === 'Escape') {
                    setEditingNode(null);
                  }
                }}
                autoFocus
              />
            ) : (
              <span onDoubleClick={() => setEditingNode({ id: node.id, label: node.label ?? '' })}>
                {node.label}
              </span>
            )}
          </span>
          <div className="node-actions">
            {node.type !== 'terminal' && (
              <div className="btn-icon-group">
                <button
                  className="btn-icon btn-icon--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddChild(node.id, 'decision');
                  }}
                  title="Add child"
                  aria-label="Add child"
                >
                  +
                </button>
                <button
                  className="btn-icon btn-icon--menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    const menu = e.currentTarget.nextElementSibling as HTMLElement;
                    if (menu) {
                      // Close any other open menu
                      if (openMenuRef.current && openMenuRef.current !== menu) {
                        openMenuRef.current.style.display = 'none';
                      }
                      const isOpen = menu.style.display === 'block';
                      menu.style.display = isOpen ? 'none' : 'block';
                      openMenuRef.current = isOpen ? null : menu;
                    }
                  }}
                  title="More actions"
                  aria-label="More actions"
                  aria-haspopup="true"
                >
                  ⌄
                </button>
                <div className="btn-icon-menu">
                  <button
                    className="btn-icon-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddChild(node.id, 'decision');
                      const menu = (e.currentTarget.closest('.btn-icon-menu') as HTMLElement);
                      if (menu) {
                        menu.style.display = 'none';
                        openMenuRef.current = null;
                      }
                    }}
                  >
                    Decision
                  </button>
                  <button
                    className="btn-icon-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddChild(node.id, 'chance');
                      const menu = (e.currentTarget.closest('.btn-icon-menu') as HTMLElement);
                      if (menu) {
                        menu.style.display = 'none';
                        openMenuRef.current = null;
                      }
                    }}
                  >
                    Chance
                  </button>
                  <button
                    className="btn-icon-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddChild(node.id, 'terminal');
                      const menu = (e.currentTarget.closest('.btn-icon-menu') as HTMLElement);
                      if (menu) {
                        menu.style.display = 'none';
                        openMenuRef.current = null;
                      }
                    }}
                  >
                    Terminal
                  </button>
                </div>
              </div>
            )}
            <button
              className="btn-icon btn-icon--danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveNode(node.id);
              }}
              title="Delete node"
              aria-label="Delete node"
            >
              ×
            </button>
          </div>
        </div>
        {Array.isArray(node.children) && node.children.map((child, index, array) => 
          renderOutline(child, depth + 1, index === array.length - 1)
        )}
      </div>
    );
  };

  return (
    <div className="decision-tree-editor">
      <div className="editor-outline" role="region" aria-label="Tree outline">
        <div
          className="section-header"
          role="button"
          tabIndex={0}
          aria-expanded={outlineExpanded}
          onClick={() => setOutlineExpanded(!outlineExpanded)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOutlineExpanded(!outlineExpanded)}
        >
          <h3>Tree Outline</h3>
          <span className="collapse-icon" aria-hidden="true">{outlineExpanded ? '▼' : '▶'}</span>
        </div>
        {outlineExpanded && (
          <div className="outline-container" role="tree" aria-label="Nodes">
            {renderOutline(tree.root)}
          </div>
        )}
      </div>
      
      <div className="editor-inspector" role="region" aria-label="Node inspector">
        <div
          className="section-header"
          role="button"
          tabIndex={0}
          aria-expanded={inspectorExpanded}
          onClick={() => setInspectorExpanded(!inspectorExpanded)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setInspectorExpanded(!inspectorExpanded)}
        >
          <h3>Node Inspector</h3>
          <span className="collapse-icon" aria-hidden="true">{inspectorExpanded ? '▼' : '▶'}</span>
        </div>
        {inspectorExpanded && (
          <>
            {selectedNode ? (
          <div className="inspector-form">
            {/* Basics Section */}
            <div className="inspector-section">
              <h4 className="inspector-section-header">Basics</h4>
              <div className="inspector-section-content">
                <div className="form-group">
                  <label htmlFor={`node-label-${selectedNode.id}`}>Label:</label>
                  <input
                    id={`node-label-${selectedNode.id}`}
                    name={`node-label-${selectedNode.id}`}
                    type="text"
                    value={selectedNode.label}
                    onChange={(e) => {
                      const newLabel = e.target.value;
                      const updates: Partial<LegacyTreeNode> = { label: newLabel };
                      
                      // Auto-categorize outcome type based on label keywords
                      // Only for terminal nodes as backend only processes nodeType for terminal nodes
                      if (selectedNode.type === 'terminal') {
                        const lower = newLabel.toLowerCase();
                        let detectedType: 'death' | 'hospitalization' | 'infection' | 'healthy' | null = null;

                        // Priority: Death > Hospitalization > Infection > Healthy
                        if (['death', 'died', 'mortality', 'fatal', 'lethal', 'deceased'].some(w => lower.includes(w))) {
                          detectedType = 'death';
                        } else if (['hosp', 'admission', 'inpatient', 'admitted', 'ward'].some(w => lower.includes(w))) {
                          detectedType = 'hospitalization';
                        } else if (['infection', 'sick', 'case', 'infected', 'illness', 'disease', 'symptomatic'].some(w => lower.includes(w))) {
                          detectedType = 'infection';
                        } else if (['healthy', 'no infection', 'recovered', 'well', 'uninfected', 'cured', 'normal', 'baseline'].some(w => lower.includes(w))) {
                          detectedType = 'healthy';
                        }

                        if (detectedType && detectedType !== selectedNode.nodeType) {
                          updates.nodeType = detectedType;
                          setAutoCategorizedMsg(`Auto-set outcome to: ${detectedType}`);
                          // Clear message after 3 seconds
                          setTimeout(() => setAutoCategorizedMsg(null), 3000);
                        }
                      }
                      
                      handleUpdateNode(selectedNode.id, updates);
                    }}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor={`node-type-${selectedNode.id}`}>Type:</label>
                  <select
                    id={`node-type-${selectedNode.id}`}
                    name={`node-type-${selectedNode.id}`}
                    value={selectedNode.type}
                    onChange={(e) => handleUpdateNode(selectedNode.id, { type: e.target.value as LegacyNodeType })}
                  >
                    <option value="decision">Decision</option>
                    <option value="chance">Chance</option>
                    <option value="terminal">Terminal</option>
                  </select>
                </div>

                {/* Node Type (Outcome Classification) - Only for Terminal Nodes */}
                {selectedNode.type === 'terminal' && (
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label htmlFor={`node-outcome-type-${selectedNode.id}`}>Outcome Type:</label>
                      {autoCategorizedMsg && (
                        <span style={{ fontSize: '11px', color: 'var(--accent)', fontStyle: 'italic', animation: 'fadeIn 0.3s' }}>
                          {autoCategorizedMsg}
                        </span>
                      )}
                    </div>
                    <select
                      id={`node-outcome-type-${selectedNode.id}`}
                      name={`node-outcome-type-${selectedNode.id}`}
                      value={selectedNode.nodeType || 'other'}
                      onChange={(e) => handleUpdateNode(selectedNode.id, { nodeType: e.target.value as any })}
                    >
                      <option value="healthy">Healthy (No Event)</option>
                      <option value="infection">Infection</option>
                      <option value="hospitalization">Hospitalization</option>
                      <option value="death">Death</option>
                      <option value="recover">Recover</option>
                      <option value="survive">Survive</option>
                      <option value="other">Other</option>
                    </select>
                    <p className="form-help-text" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                      Used to calculate epidemiological metrics (infections, hospitalizations, deaths)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Child Probabilities Section */}
            {selectedNode.type === 'chance' && selectedNode.children?.length ? (
              <div className="inspector-section">
                <h4 className="inspector-section-header">Child probabilities</h4>
                <div className="inspector-section-content">
                  <div className="prob-table">
                    <div className="prob-header-row">
                      <div className="prob-header-cell">Outcome</div>
                      <div className="prob-header-cell">Probability</div>
                    </div>
                    {selectedNode.children.map((child) => (
                      <div key={child.id} className="prob-row">
                        <span className="prob-child-label">{child.label ?? child.id}</span>
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.01"
                          value={typeof child.p === 'number' ? child.p : ''}
                          onChange={(e) => {
                            const v = e.currentTarget.value.trim();
                            const val = v === '' ? undefined : Math.max(0, Math.min(1, parseFloat(v)));
                            // legacy stores edge prob on child
                            updateChanceProbability(child.id, val);
                          }}
                          aria-label={`Probability for ${child.label ?? child.id}`}
                        />
                      </div>
                    ))}
                    <div className="prob-sum">
                      Sum:&nbsp;
                      {(() => {
                        const sum = selectedNode.children.reduce(
                          (acc, c) => acc + (typeof c.p === 'number' ? c.p : 0),
                          0
                        );
                        const ok = Math.abs(sum - 1) < 1e-6;
                        return <span style={{ color: ok ? 'var(--ok)' : 'var(--danger)' }}>{sum.toFixed(2)}</span>;
                      })()}
                      {(() => {
                        const sum = selectedNode.children.reduce(
                          (acc, c) => acc + (typeof c.p === 'number' ? c.p : 0),
                          0
                        );
                        const ok = Math.abs(sum - 1) < 1e-6;
                        if (!ok && sum > 0) {
                          return (
                            <button
                              className="btn btn-xs btn-secondary"
                              style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '10px' }}
                              onClick={() => handleNormalizeProbabilities(selectedNode.id)}
                              title="Scale probabilities to sum to 1.0"
                            >
                              Normalize
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Transition Costs Section */}
            {selectedNode.children?.length ? (
              <div className="inspector-section">
                <h4 className="inspector-section-header">Transition costs</h4>
                <p className="form-help-text" style={{ marginTop: '-4px', marginBottom: '12px', fontSize: '10px' }}>Costs (€) and utility losses for each transition</p>
                <div className="inspector-section-content">
                  <div className="transition-table">
                    {selectedNode.children.map((child) => (
                      <div key={child.id} className="transition-row">
                        <div className="transition-child-label">{child.label ?? child.id}</div>
                        <div className="transition-inputs">
                          <div className="transition-input-group">
                            <label htmlFor={`transition-cost-${child.id}`}>Cost (€):</label>
                            <input
                              id={`transition-cost-${child.id}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0"
                              value={typeof child.transition_cost === 'number' ? child.transition_cost : ''}
                              onChange={(e) => {
                                const v = e.currentTarget.value.trim();
                                const val = v === '' ? undefined : Math.max(0, parseFloat(v));
                                handleUpdateNode(child.id, { transition_cost: val } as any);
                              }}
                              aria-label={`Transition cost for ${child.label ?? child.id}`}
                            />
                          </div>
                          <div className="transition-input-group">
                            <label htmlFor={`transition-utility-${child.id}`}>QALY Loss:</label>
                            <input
                              id={`transition-utility-${child.id}`}
                              type="number"
                              min="0"
                              max="1"
                              step="0.001"
                              placeholder="0"
                              value={typeof child.transition_utility_loss === 'number' ? child.transition_utility_loss : ''}
                              onChange={(e) => {
                                const v = e.currentTarget.value.trim();
                                const val = v === '' ? undefined : Math.max(0, Math.min(1, parseFloat(v)));
                                handleUpdateNode(child.id, { transition_utility_loss: val } as any);
                              }}
                              aria-label={`Transition utility loss for ${child.label ?? child.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Node Totals Section */}
            <div className="inspector-section">
              <h4 className="inspector-section-header">Node totals</h4>
              <div className="inspector-section-content">
                <div className="form-group">
                  <label htmlFor={`node-cost-${selectedNode.id}`}>Cost (€):</label>
                  <input
                    id={`node-cost-${selectedNode.id}`}
                    name={`node-cost-${selectedNode.id}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={selectedNode.cost_eur || ''}
                    onChange={(e) => handleUpdateNode(selectedNode.id, { cost_eur: parseFloat(e.target.value) || undefined })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`node-qaly-${selectedNode.id}`}>QALY:</label>
                  <input
                    id={`node-qaly-${selectedNode.id}`}
                    name={`node-qaly-${selectedNode.id}`}
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={selectedNode.qaly || ''}
                    onChange={(e) => handleUpdateNode(selectedNode.id, { qaly: parseFloat(e.target.value) || undefined })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`node-notes-${selectedNode.id}`}>Notes:</label>
                  <textarea
                    id={`node-notes-${selectedNode.id}`}
                    name={`node-notes-${selectedNode.id}`}
                    value={selectedNode.notes || ''}
                    onChange={(e) => handleUpdateNode(selectedNode.id, { notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
            </div>


          </div>
            ) : (
              <div className="inspector-empty">
                <p>Select a node to edit its properties</p>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmationDialog
        isOpen={pendingRemoveNodeId !== null}
        title="Remove node?"
        message="Are you sure you want to remove this node and all its children?"
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmRemoveNode}
        onCancel={() => setPendingRemoveNodeId(null)}
      />
    </div>
  );
};

// Helper function to find a node in the tree
const findNodeInTree = (node: LegacyTreeNode, nodeId: string): LegacyTreeNode | null => {
  if (node.id === nodeId) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeInTree(child, nodeId);
      if (found) return found;
    }
  }
  return null;
};

export default DecisionTreeEditor;
